import { DEFAULT_RECEIPT_MS, DEFAULT_SIGNATURE_MS } from '../constants/constants';
import type { Hash } from 'viem';

export type TxStage = 'awaiting_signature' | 'awaiting_receipt' | 'success' | 'error';

export interface IOrchestratorError {
  code: string;
  message: string;
  cause?: unknown;
}

interface IOrchestratorOptions<TReceipt = unknown> {
  signatureMs?: number;
  receiptMs?: number;
  /** Optional: decide success based on receipt; if omitted we assume success. */
  isSuccess?: (r: TReceipt) => boolean;
}

interface IOrchestratorCallbacks<TReceipt = unknown> {
  onTxTimeout?: () => void;
  onTx?: (tx: Hash) => void;
  onReceiptTimeout?: () => void;
  onReceipt?: (receipt: TReceipt) => void;
  onError?: (err: IOrchestratorError) => void;
  onFinally?: () => void;
}

export async function orchestrateTx<TReceipt = unknown>(
  send: () => Promise<Hash>,
  waitForReceipt: (tx: Hash) => Promise<TReceipt>,
  opts: IOrchestratorOptions<TReceipt> = {},
  cbs: IOrchestratorCallbacks<TReceipt> = {},
): Promise<TReceipt> {
  const { signatureMs = DEFAULT_SIGNATURE_MS, receiptMs = DEFAULT_RECEIPT_MS, isSuccess } = opts;
  const { onTxTimeout, onTx, onReceiptTimeout, onReceipt, onError, onFinally } = cbs;

  let sigTimer: ReturnType<typeof setTimeout> | undefined;
  let rcptTimer: ReturnType<typeof setTimeout> | undefined;
  let stage: TxStage = 'awaiting_signature';

  const safe =
    <T extends (...args: unknown[]) => unknown>(fn?: T) =>
    (...args: Parameters<T>): ReturnType<T> | void => {
      try {
        return fn?.(...args) as ReturnType<T>;
      } catch (e: any) {
        stage = 'error';
        const err: IOrchestratorError = {
          code: String(e?.code ?? e?.name ?? 'TX_ERROR'),
          message: e?.shortMessage || e?.message || 'Transaction error',
          cause: e,
        };
        safeOnError(err);
        throw err;
      }
    };

  const safeOnTxTimeout = safe(onTxTimeout);
  const safeOnTx = safe(onTx);
  const safeOnReceiptTimeout = safe(onReceiptTimeout);
  const safeOnReceipt = safe(onReceipt);
  const safeOnError = safe(onError);
  const safeOnFinally = safe(onFinally);

  const withTimeout = (
    ms: number,
    expectedStage: TxStage,
    callback: () => void,
  ): ReturnType<typeof setTimeout> => {
    return setTimeout(() => {
      if (stage === expectedStage) callback();
    }, ms);
  };

  try {
    // Phase 1 — awaiting signature
    sigTimer = withTimeout(signatureMs, 'awaiting_signature', safeOnTxTimeout);

    const tx = await send();

    if (sigTimer) clearTimeout(sigTimer);
    stage = 'awaiting_receipt';
    safeOnTx(tx);

    // Phase 2 — awaiting receipt
    rcptTimer = withTimeout(receiptMs, 'awaiting_receipt', safeOnReceiptTimeout);

    const receipt = await waitForReceipt(tx);

    if (rcptTimer) clearTimeout(rcptTimer);

    // Optional success detection
    if (!isSuccess || isSuccess(receipt)) {
      stage = 'success';
    } else {
      stage = 'error';
      safeOnError({
        code: 'REVERTED',
        message: 'Transaction reverted',
        cause: receipt,
      });
    }

    safeOnReceipt(receipt);
    return receipt;
  } catch (e: any) {
    stage = 'error';
    const err: IOrchestratorError = {
      code: String(e?.code ?? e?.name ?? 'TX_ERROR'),
      message: e?.shortMessage || e?.message || 'Transaction error',
      cause: e,
    };
    safeOnError(err);
    throw err;
  } finally {
    if (sigTimer) clearTimeout(sigTimer);
    if (rcptTimer) clearTimeout(rcptTimer);
    safeOnFinally();
  }
}
